import {
  extendTheme,
  Theme,
  ThemeConfig,
  ThemeOverride,
} from '@chakra-ui/react'
import { Styles } from '@chakra-ui/theme-tools'
import {
  Accordion,
  Button,
  Checkbox,
  Container,
  FormLabel,
  Input,
  Modal,
  Popover,
  Progress,
  Radio,
  Select,
  Table,
  Tabs,
  Textarea,
  Menu,
  Tooltip,
} from './components'
import { scrollbar } from './scrollbar'

const DEFAULT_FONT_FAMILY = 'Poppins'

type THexColor = `#${string}`
export enum BodyBackgroundNuance {
  Clean = 'clean',
  Contained = 'contained'
}

export type TColorIndexKey = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
export type TSubColorIndexKey = 1 | 2 | 3 | 4 | 5 | 6 | 7
export type TSubColorNameKey =
  | 'blue'
  | 'green'
  | 'orange'
  | 'pink'
  | 'purple'
  | 'red'
  | 'yellow'
export type TColorNameKey =
  | 'blue'
  | 'green'
  | 'grey'
  | 'orange'
  | 'purple'
  | 'red'
  | 'white'
  | 'yellow'

type TColorSet<T extends number | string = TColorIndexKey> = {
  dark: Record<T, THexColor>
  light: Record<T, THexColor>
}

type TSubColors = Record<TSubColorNameKey, TColorSet<TSubColorIndexKey>>

export type TRw1ColorNamespace = TSubColors & {
  colors: TColorSet<TColorNameKey>
  highlight: TColorSet['dark'] // it doesn't matter, pick one for the type.
  primary: TColorSet
  secondary: TColorSet
  text: TColorSet
  body: TColorSet<BodyBackgroundNuance>
}

export const colors: Partial<Theme['colors']> & {
  rw1: TRw1ColorNamespace
} = {
  rw1: {
    body: {
      dark: {
        contained: '#2C2C2E',
        clean: '#09090A',
      },
      light: {
        contained: '#E5E5E5',
        clean: '#FFFFFF',
      },
    },
    colors: {
      dark: {
        blue: '#64D2FF',
        green: '#32D74B',
        orange: '#FF9F0A',
        purple: '#5E5CE6',
        red: '#FF453A',
        yellow: '#FFD60A',
        white: '#FFFFFF',
        grey: '#8E8E93',
      },
      light: {
        blue: '#5AC8FA',
        green: '#34C759',
        orange: '#FF9500',
        purple: '#5856D6',
        red: '#FF483D',
        yellow: '#FFCC00',
        white: '#FFFFFF',
        grey: '#AEAEB2',
      },
    },
    highlight: {
      0: '#2B9AFF',
      1: '#45A6FF',
      2: '#5EB2FF',
      3: '#78BEFF',
      4: '#91CBFF',
      5: '#ABD7FF',
      6: '#C4E3FF',
      7: '#DEEFFF',
      8: '#F7FBFF',
    },
    blue: {
      dark: {
        1: '#151D28',
        2: '#28405D',
        3: '#39618E',
        4: '#1D82F7',
        5: '#7CB6F8',
        6: '#B9D9FF',
        7: '#F5F9FF',
      },
      light: {
        1: '#F5F9FF',
        2: '#B9D9FF',
        3: '#7CB6F8',
        4: '#288BFE',
        5: '#39618E',
        6: '#28405D',
        7: '#151D28',
      },
    },
    green: {
      dark: {
        1: '#15281F',
        2: '#25553E',
        3: '#377B5A',
        4: '#2FD075',
        5: '#81EEBA',
        6: '#B6F7D6',
        7: '#F0FFF8',
      },
      light: {
        1: '#F0FFF8',
        2: '#B6F7D6',
        3: '#81EEBA',
        4: '#2DD282',
        5: '#377B5A',
        6: '#25553E',
        7: '#15281F',
      },
    },
    orange: {
      dark: {
        1: '#2E2619',
        2: '#644C2B',
        3: '#9E7133',
        4: '#FF8C00',
        5: '#FFC470',
        6: '#FFDBA8',
        7: '#FFF9F0',
      },
      light: {
        1: '#FFF9F0',
        2: '#FFDBA8',
        3: '#FFC470',
        4: '#FF9914',
        5: '#9E7133',
        6: '#644C2B',
        7: '#2E2619',
      },
    },
    pink: {
      dark: {
        1: '#2E1922',
        2: '#642B42',
        3: '#8B3C5B',
        4: '#FB377F',
        5: '#FF9EC5',
        6: '#FFC2DB',
        7: '#FFF5F9',
      },
      light: {
        1: '#FFF5F9',
        2: '#FFC2DB',
        3: '#FF9EC5',
        4: '#FA2E80',
        5: '#8B3C5B',
        6: '#642B42',
        7: '#2E1922',
      },
    },
    purple: {
      dark: {
        1: '#28192E',
        2: '#42294C',
        3: '#644072',
        4: '#AC44D5',
        5: '#DFA1F7',
        6: '#F0CFFE',
        7: '#FCF5FF',
      },
      light: {
        1: '#FCF5FF',
        2: '#F0CFFE',
        3: '#DFA1F7',
        4: '#B24EDA',
        5: '#644072',
        6: '#42294C',
        7: '#28192E',
      },
    },
    red: {
      dark: {
        1: '#2B1917',
        2: '#612E29',
        3: '#8D3C34',
        4: '#FF453A',
        5: '#FC8479',
        6: '#FFA299',
        7: '#FFF6F5',
      },
      light: {
        1: '#FFF6F5',
        2: '#FFA299',
        3: '#FC8479',
        4: '#FF483D',
        5: '#8D3C34',
        6: '#612E29',
        7: '#2B1917',
      },
    },
    yellow: {
      dark: {
        1: '#2E2A19',
        2: '#64592B',
        3: '#968231',
        4: '#F9BC06',
        5: '#FFE16B',
        6: '#FCE99C',
        7: '#FFFCF0',
      },
      light: {
        1: '#FFFCF0',
        2: '#FCE99C',
        3: '#FFE16B',
        4: '#F9C806',
        5: '#968231',
        6: '#64592B',
        7: '#2E2A19',
      },
    },
    primary: {
      dark: {
        0: '#001834',
        1: '#003878',
        2: '#0056B9',
        3: '#0C6FE2',
        4: '#288BFE',
        5: '#52A2FF',
        6: '#7AB8FF',
        7: '#A4CEFF',
        8: '#CDE4FF',
      },
      light: {
        0: '#CDE4FF',
        1: '#A4CEFF',
        2: '#7AB8FF',
        3: '#52A2FF',
        4: '#288BFE',
        5: '#0C6FE2',
        6: '#0056B9',
        7: '#003878',
        8: '#001834',
      },
    },
    secondary: {
      dark: {
        0: '#2C2C2E',
        1: '#3A3A3C',
        2: '#48484A',
        3: '#636366',
        4: '#8E8E93',
        5: '#AEAEB2',
        6: '#C7C7CC',
        7: '#D1D1D6',
        8: '#E5E5EA',
      },
      light: {
        0: '#F8F8FB',
        1: '#E5E5EA',
        2: '#D1D1D6',
        3: '#C7C7CC',
        4: '#AEAEB2',
        5: '#8E8E93',
        6: '#636366',
        7: '#48484A',
        8: '#3A3A3C',
      },
    },
    text: {
      dark: {
        0: '#8E8E93',
        1: '#AEAEB2',
        2: '#C7C7CC',
        3: '#D1D1D6',
        4: '#E5E5EA',
        5: '#EDEDF2',
        6: '#FCFCFC',
        7: '#09090A',
        8: '#FCFCFC',
      },
      light: {
        0: '#8E8E93',
        1: '#636366',
        2: '#48484A',
        3: '#3A3A3C',
        4: '#2C2C2E',
        5: '#1C1C1E',
        6: '#09090A',
        7: '#09090A',
        8: '#FCFCFC',
      },
    },
  },
}

const components: ThemeOverride['components'] = {
  Accordion,
  Button,
  Checkbox,
  Container,
  FormLabel,
  Input,
  Modal,
  Popover,
  Progress,
  Radio,
  Select,
  Table,
  Tabs,
  Textarea,
  Tooltip,
  Menu,
}

const fonts: Theme['fonts'] = {
  body: DEFAULT_FONT_FAMILY,
  heading: DEFAULT_FONT_FAMILY,
  mono: DEFAULT_FONT_FAMILY,
}

const fontSizes: Theme['fontSizes'] = {
  '2xl': '1.5rem', // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem', // 36px
  '5xl': '3rem', // 48px
  '6xl': '4rem', // 64px
  '7xl': '5rem', // 80px
  '8xl': '6rem', // 96px
  '9xl': '7rem', // 112px
  lg: '1rem', // 16px
  md: '0.875rem', // 14px
  sm: '0.75rem', // 12px
  xl: '1.125rem', // 18px
  xs: '0.625rem', // 10px
  '2xs': '0.5rem', // 8px
  '3xs': '0.25rem', // 4px
}

const shadows: Partial<Theme['shadows']> & {
  rw1: { base: string; text: string }
} = {
  outline: '0 0 0 0',
  rw1: {
    base: '0 0px 6px 0px rgba(0, 0, 0, 0.1), 0 4px 4px -1px rgba(0, 0, 0, 0.2)',
    text: '0px 0px 8px rgba(0, 0, 0, 0.5), 0px 0px 32px #000000',
  },
}

const breakpoints = {
  android: '960px',
}

const styles = (bodyType: BodyBackgroundNuance, isAndroid?: boolean): Styles => ({
  global: (props) => {
    const backgroundColor = { backgroundColor: colors.rw1.body[props.colorMode][bodyType] }
    const appStyle = {

      margin: '0px',
      overflow: 'hidden',
      height: '100vh',
    }
    const borderRadius = isAndroid ? { borderRadius: '0px', } : { borderRadius: '10px' }
    const boxShadow = isAndroid ? { boxShadow: '0px 0px 0px rgba(0, 0, 0, 0)' } : { boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.5)', }

    return {
      body: {
        backgroundColor: 'transparent',
        overflow: 'hidden',
      },
      '#app': { appStyle, ...borderRadius, ...boxShadow, ...backgroundColor },
      '[data-not-focus]:focus': {
        outline: '0 !important',
        boxShadow: '0 0 0 0 rgba(0, 0, 0, 0) !important',
      },
      ...scrollbar({
        colorMode: props.colorMode,
        thumbColor: {
          dark: colors.rw1.secondary.dark['3'],
          light: colors.rw1.secondary.light['2'],
        },
      }),
    }
  },
})

// This is the core theme provider for the app.
// Based on https://www.figma.com/file/xWsZ0oxRTk2fZ5LZzYKRom/Design-System-2.0
// Check https://github.com/chakra-ui/chakra-ui/tree/main/packages/theme/src/components
export const setupTheme = (
  config: ThemeConfig,
  bodyType: BodyBackgroundNuance,
  isAndroid?: boolean
): ReturnType<typeof extendTheme> =>
  extendTheme({
    breakpoints,
    colors,
    components,
    config,
    fonts,
    fontSizes,
    shadows,
    styles: styles(bodyType, isAndroid),
  })
